import React, { useState, useCallback, useEffect } from "react";
import {
  Box, Typography, Paper, Button, ButtonGroup, TextField, IconButton,
  Tooltip, Chip, Drawer, CircularProgress,
} from "@mui/material";
import {
  Feedback as FeedbackIcon,
  OpenInNew as OpenInNewIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { authFetch } from "../../utils/authHelpers";
import { BACKEND_URL } from "../../utils/apiConfig";

const WEBSITE_BASE = "https://fullframe-website.vercel.app";

const QUICK_PAGES = [
  { path: "/nl", label: "Home" },
  { path: "/nl/propositions", label: "Propositions" },
  { path: "/nl/work", label: "Work" },
  { path: "/nl/approach", label: "Approach" },
  { path: "/nl/ai", label: "AI" },
  { path: "/nl/insights", label: "Insights" },
  { path: "/nl/about", label: "About" },
  { path: "/nl/contact", label: "Contact" },
];

export default function WebsitePreview() {
  const [url, setUrl] = useState(`${WEBSITE_BASE}/nl`);
  const [inputUrl, setInputUrl] = useState(`${WEBSITE_BASE}/nl`);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const iframeRef = React.useRef(null);

  const navigate = (path) => {
    const newUrl = `${WEBSITE_BASE}${path}`;
    setUrl(newUrl);
    setInputUrl(newUrl);
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    setUrl(inputUrl);
  };

  const refreshIframe = () => {
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  const fetchRecentFeedback = useCallback(async () => {
    setLoadingFeedback(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/Api/feedback/my?target_repo=websiteff&limit=10`
      );
      if (res.ok) {
        const data = await res.json();
        setRecentFeedback(data);
      }
    } catch (err) {
      console.error("[WebsitePreview] Failed to load feedback:", err);
    } finally {
      setLoadingFeedback(false);
    }
  }, []);

  useEffect(() => {
    if (sidebarOpen) fetchRecentFeedback();
  }, [sidebarOpen, fetchRecentFeedback]);

  // Detect current page from URL for feedback context
  const currentPage = QUICK_PAGES.find((p) => url.includes(p.path))?.label || "Website";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
      {/* Toolbar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          borderRadius: 2,
          mb: 1.5,
        }}
      >
        {/* Quick nav buttons */}
        <ButtonGroup size="small" variant="outlined">
          {QUICK_PAGES.map(({ path, label }) => (
            <Button
              key={path}
              onClick={() => navigate(path)}
              sx={{
                textTransform: "none",
                fontWeight: url.includes(path) ? 700 : 400,
                bgcolor: url.includes(path) ? "#e0e7ff" : "transparent",
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>

        {/* URL bar */}
        <Box
          component="form"
          onSubmit={handleUrlSubmit}
          sx={{ flex: 1, display: "flex", gap: 0.5, minWidth: 200 }}
        >
          <TextField
            size="small"
            fullWidth
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="URL..."
            sx={{ "& .MuiInputBase-input": { fontSize: "0.85rem", py: 0.75 } }}
          />
          <Tooltip title="Vernieuwen">
            <IconButton size="small" onClick={refreshIframe}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Actions */}
        <Tooltip title="Open in nieuw tabblad">
          <IconButton size="small" onClick={() => window.open(url, "_blank")}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          size="small"
          startIcon={<FeedbackIcon />}
          onClick={() => {
            // Dispatch custom event to open FeedbackFAB with websiteff context
            window.dispatchEvent(
              new CustomEvent("open-feedback", {
                detail: { targetRepo: "websiteff", page: currentPage },
              })
            );
          }}
          sx={{
            textTransform: "none",
            bgcolor: "#6366f1",
            "&:hover": { bgcolor: "#4f46e5" },
          }}
        >
          Feedback
        </Button>

        <Tooltip title="Recente feedback">
          <IconButton size="small" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Main content: iframe + optional sidebar */}
      <Box sx={{ flex: 1, display: "flex", gap: 1.5, minHeight: 0 }}>
        {/* iframe */}
        <Paper
          variant="outlined"
          sx={{ flex: 1, borderRadius: 2, overflow: "hidden", position: "relative" }}
        >
          <iframe
            ref={iframeRef}
            src={url}
            title="Full Frame Website Preview"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        </Paper>

        {/* Sidebar: recent feedback */}
        <Drawer
          variant="persistent"
          anchor="right"
          open={sidebarOpen}
          PaperProps={{
            sx: {
              position: "relative",
              width: 320,
              border: "none",
              bgcolor: "transparent",
            },
          }}
          sx={{ flexShrink: 0 }}
        >
          {sidebarOpen && (
            <Paper variant="outlined" sx={{ p: 2, height: "100%", overflow: "auto", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                Recente website feedback
              </Typography>

              {loadingFeedback ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recentFeedback.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nog geen feedback voor de website.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {recentFeedback.map((item) => (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 1.5 }}
                    >
                      <Box sx={{ display: "flex", gap: 1, mb: 0.5, alignItems: "center" }}>
                        <Chip
                          label={item.feedback_type}
                          size="small"
                          sx={{ fontSize: "0.65rem", height: 20 }}
                        />
                        <Chip
                          label={item.tool_name}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            bgcolor: "#e0e7ff",
                            color: "#4338ca",
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: "auto" }}
                        >
                          {new Date(item.created_at).toLocaleDateString("nl-NL", {
                            day: "numeric",
                            month: "short",
                          })}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.description}
                      </Typography>
                      <Chip
                        label={item.status}
                        size="small"
                        sx={{ mt: 0.5, fontSize: "0.6rem", height: 18 }}
                        variant="outlined"
                      />
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          )}
        </Drawer>
      </Box>
    </Box>
  );
}
